import { Tour } from '../types';

/**
 * Apply round-robin (interleaved) ordering to tours based on category-specific orders.
 *
 * Pattern:
 *   Slot 1  → Position-1 tour of Category A
 *   Slot 2  → Position-1 tour of Category B
 *   Slot 3  → Position-1 tour of Category C
 *   Slot 4  → Position-2 tour of Category A
 *   Slot 5  → Position-2 tour of Category B
 *   ...
 *
 * - Category sequence follows `categories` array (sorted by sort_order / sortOrder).
 *   If `categories` is not provided, the sequence is derived from the tours themselves.
 * - Within each category, tours are sorted by their own `sortOrder` (ascending),
 *   with a `createdAt` fallback when sortOrders are equal.
 *
 * @param tours      Full list of tours to interleave.
 * @param categories Optional ordered list of category objects (must have `id` field).
 *                   Pass the array from `CategoryOrderContext` for correct ordering.
 * @returns Interleaved tour list.
 */
export function applyRoundRobinOrder(tours: Tour[], categories?: any[]): Tour[] {
    if (!tours || tours.length === 0) return tours;

    try {
        // ── 1. Determine the category sequence ────────────────────────────────
        const categoryIdsInOrder: string[] = [];

        if (categories && categories.length > 0) {
            // Use the admin-provided category order (already sorted by sortOrder in context).
            // We sort defensively in case the caller didn't pre-sort.
            const sortedCategories = [...categories].sort(
                (a, b) => (a.sortOrder ?? a.sort_order ?? 0) - (b.sortOrder ?? b.sort_order ?? 0)
            );
            sortedCategories.forEach(cat => categoryIdsInOrder.push(cat.id));
        }

        // ── 2. Group tours by category (prefer categoryId, fall back to name) ─
        const categoryMap = new Map<string, Tour[]>();

        // Sort tours within the same category by their own sortOrder first
        const toursPreSorted = [...tours].sort((a, b) => {
            const orderDiff = (a.sortOrder || 0) - (b.sortOrder || 0);
            if (orderDiff !== 0) return orderDiff;
            // Tie-break: newer tours first when sortOrders are identical
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

        // Create a lookup map for faster category resolution by name
        const categoryNameMap = new Map<string, string>();
        if (categories) {
            categories.forEach(cat => {
                if (cat.name) {
                    categoryNameMap.set(cat.name.toLowerCase().trim(), cat.id);
                }
            });
        }

        for (const tour of toursPreSorted) {
            // Priority 1: Exact categoryId match
            let catKey = (tour as any).categoryId || (tour as any).category_id;

            // Priority 2: Match by category name string against the context array
            if (!catKey && tour.category) {
                const matchedId = categoryNameMap.get(tour.category.toLowerCase().trim());
                if (matchedId) {
                    catKey = matchedId;
                }
            }

            // Priority 3: Fallback to the raw category string (or 'unknown') if no match exists
            if (!catKey) {
                catKey = tour.category || 'unknown';
            }

            if (!categoryMap.has(catKey)) {
                categoryMap.set(catKey, []);
                // If we haven't seen this category yet and it's not in the admin list, append it
                if (!categoryIdsInOrder.includes(catKey)) {
                    categoryIdsInOrder.push(catKey);
                }
            }
            categoryMap.get(catKey)!.push(tour);
        }

        // ── 3. Interleave ─────────────────────────────────────────────────────
        let maxToursInCategory = 0;
        categoryMap.forEach(catTours => {
            if (catTours.length > maxToursInCategory) maxToursInCategory = catTours.length;
        });

        const roundRobinTours: Tour[] = [];

        for (let position = 0; position < maxToursInCategory; position++) {
            for (const catId of categoryIdsInOrder) {
                const catTours = categoryMap.get(catId);
                if (catTours && position < catTours.length) {
                    roundRobinTours.push(catTours[position]);
                }
            }
        }

        return roundRobinTours;
    } catch (error) {
        console.error('Error applying round-robin tour order:', error);
        return tours;
    }
}
